import React from "react";
import Layout from "../../Components/Layout";
import {
  Box,
  Grid,
  Typography,
  Drawer,
  Button,
  IconButton,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import RedoIntegration from "./components/RedoIntegration";
import UPSIntegration from "./components/UPSIntegration";
import ShopifyIntegration from "./components/ShopifyIntegration";
import OutlookIntegration from "./components/OutlookIntegration";
import GmailIntegration from "./components/GmailIntegration";
import { useDispatch, useSelector } from "react-redux";
import useCallOnce from "../../hooks/useCallOnce";
import { getUserById } from "../../redux/actions/userActions";

function Integration({ selectedUserId }) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedCategory, setSelectedCategory] =
    React.useState("All categories");
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
  const dispatch = useDispatch();
  const { byId: userById } = useSelector((s) => s.users);

  // Fetch once per selectedUserId
  useCallOnce("integration/userById/init", () => {
    if (selectedUserId) dispatch(getUserById(selectedUserId));
  }, [dispatch, selectedUserId]);

  const selectedUserPayload = userById?.data?.data || userById?.data;
  const selectedUser = selectedUserPayload?.user || selectedUserPayload;

  const handleSearchChange = React.useCallback((e) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
  }, []);

  const handleSearchSubmit = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const categories = [
    "All categories",
    "Ecommerce",
    "Email Management",
    "Return and Exchanges",
    "Shipping and Fulfillment",
  ];

  const integrations = [
    { component: ShopifyIntegration, name: "Shopify", category: "Ecommerce" },
    {
      component: RedoIntegration,
      name: "Redo",
      category: "Return and Exchanges",
    },
    {
      component: UPSIntegration,
      name: "UPS",
      category: "Shipping and Fulfillment",
    },
    {
      component: OutlookIntegration,
      name: "Outlook",
      category: "Email Management",
    },
    { component: GmailIntegration, name: 'Gmail', category: 'Email Management' },
  ];

  const filteredIntegrations = integrations.filter((integration) => {
    const matchesSearch = integration.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All categories" ||
      integration.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const renderFilterContent = () => (
    <Box
      sx={{
        bgcolor: "#ffffff",
        borderRadius: { xs: 0, md: 2 },
        border: { xs: "none", md: "1px solid #e5e7eb" },
        p: 3,
        height: "100%",
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, mb: 2, display: { xs: "none", md: "block" } }}
      >
        Filters
      </Typography>

      {/* Search Input */}
      <form onSubmit={handleSearchSubmit} onClick={(e) => e.stopPropagation()}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #e5e7eb",
            borderRadius: 1,
            px: 2,
            py: 1,
            mb: 3,
            bgcolor: "#f9fafb",
          }}
        >
          <Typography sx={{ color: "#6b7280", mr: 1 }}>🔍</Typography>
          <input
            type="text"
            placeholder="Search for an app"
            value={searchTerm}
            onChange={handleSearchChange}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-lpignore="true"
            data-form-type="other"
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              width: "100%",
              fontSize: "14px",
              color: "#111827",
            }}
          />
        </Box>
      </form>

      {/* Categories */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Categories
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {categories.map((category) => (
          <Box
            key={category}
            onClick={() => {
              setSelectedCategory(category);
              if (filterDrawerOpen) setFilterDrawerOpen(false);
            }}
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 1,
              cursor: "pointer",
              bgcolor:
                selectedCategory === category ? "#f3f4f6" : "transparent",
              color: selectedCategory === category ? "#111827" : "#6b7280",
              fontWeight: selectedCategory === category ? 600 : 400,
              fontSize: "14px",
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: "#f3f4f6",
              },
            }}
          >
            {category}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    // <Layout>
    <Box sx={{ maxWidth: 1400, mt: 3, px: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Integrations
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 3, color: "#6b7280" }}>
        Connect your favorite tools and services to enhance your AI agent's
        capabilities.
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}></Typography>

        {/* Mobile Filter Button */}
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setFilterDrawerOpen(true)}
          sx={{
            display: { xs: "flex", md: "none" },
            borderColor: "#e5e7eb",
            color: "#374151",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Filters
        </Button>
      </Box>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="left"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            mt: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {renderFilterContent()}
      </Drawer>

      {/* Main Content with Filter Sidebar */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* Left Sidebar - Filters (Desktop Only) */}
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            display: { xs: "none", md: "block" },
          }}
        >
          <Box sx={{ position: "sticky", top: 20 }}>
            {renderFilterContent()}
          </Box>
        </Box>

        {/* Right Side - Integration Cards */}
        <Box sx={{ flex: 1 }}>
          <Grid container spacing={3}>
            {filteredIntegrations.map((integration, index) => {
              const Component = integration.component;
              return (
                <Grid
                  key={index}
                  size={{ xs: 12, lg: 6, md: 6, sm: 12, xl: 4 }}
                >
                  <Component selectedUserId={selectedUserId} selectedUser={selectedUser} />
                </Grid>
              );
            })}
            {/* {filteredIntegrations.map((integration, index) => {
                const Component = integration.component;
                return (
                  <Grid key={index} size={{ xs: 12, lg: 6, md: 6, sm: 12, xl: 4 }}>
                    <Component />
                  </Grid>
                );
              })} */}
          </Grid>

          {filteredIntegrations.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body1" sx={{ color: "#6b7280", mb: 1 }}>
                No integrations found
              </Typography>
              <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                Try adjusting your search or filters
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
    // </Layout>
  );
}

export default Integration;
